/**
 * Status bar — sits between the topbar and the editor body. Houses
 * undo / redo, canvas zoom controls, ratio + custom size, safe ratio,
 * and clip duration. (These used to live in the TopBar; moved here so the
 * TopBar can stay slim with action buttons only — better on mobile.)
 */

import {
  canRedo,
  canUndo,
  getState,
  redo,
  setPlayhead,
  setSelectMode,
  setZoom,
  subscribe,
  undo,
  updateProject,
} from '../store/project';
import { RATIO_DIMS, aspectFor } from '../types';
import type { Ratio } from '../types';

export class StatusBar {
  private root: HTMLElement;
  private ratioSelect!: HTMLSelectElement;
  private safeSelect!: HTMLSelectElement;
  private durationInput!: HTMLInputElement;
  private zoomLabel!: HTMLElement;
  private wInput!: HTMLInputElement;
  private hInput!: HTMLInputElement;

  constructor(root: HTMLElement) {
    this.root = root;
    this.build();
    subscribe((s) => {
      this.ratioSelect.value = s.project.canvas.ratio;
      this.safeSelect.value = s.project.canvas.safeRatio ?? 'none';
      this.durationInput.value = String(s.project.duration);
      this.zoomLabel.textContent = `${Math.round(s.displayScale * 100)}%`;
      const c = s.project.canvas.customSize ?? RATIO_DIMS[s.project.canvas.ratio];
      const wv = String(c.w);
      const hv = String(c.h);
      if (document.activeElement !== this.wInput && this.wInput.value !== wv) this.wInput.value = wv;
      if (document.activeElement !== this.hInput && this.hInput.value !== hv) this.hInput.value = hv;
    });
  }

  private build(): void {
    while (this.root.firstChild) this.root.removeChild(this.root.firstChild);

    this.root.appendChild(this.makeUndoRedo());
    this.root.appendChild(this.makeZoomControls());
    this.root.appendChild(this.labeled('비율', this.makeRatioSelect()));
    this.root.appendChild(this.makeCustomSize());
    this.root.appendChild(this.labeled('Safe', this.makeSafeSelect()));
    this.root.appendChild(this.labeled('길이(ms)', this.makeDurationInput()));
    this.root.appendChild(this.labeled('선택', this.makeSelectModeSelect()));
  }

  private makeSelectModeSelect(): HTMLElement {
    const sel = document.createElement('select');
    sel.className = 'select';
    const optAuto = document.createElement('option');
    optAuto.value = 'auto';
    optAuto.textContent = '자동선택';
    optAuto.title = '캔버스의 레이어를 누르면 그게 선택됩니다.';
    const optSticky = document.createElement('option');
    optSticky.value = 'sticky';
    optSticky.textContent = '선택이동';
    optSticky.title = '선택해둔 레이어가 캔버스 아무 곳을 눌러도 이동합니다.';
    sel.append(optAuto, optSticky);
    sel.value = getState().selectMode;
    sel.addEventListener('change', () => {
      setSelectMode(sel.value === 'sticky' ? 'sticky' : 'auto');
    });
    subscribe((s) => {
      if (sel.value !== s.selectMode) sel.value = s.selectMode;
    });
    return sel;
  }

  private labeled(label: string, control: HTMLElement): HTMLElement {
    const g = document.createElement('div');
    g.className = 'group';
    const l = document.createElement('span');
    l.className = 'label';
    l.textContent = label;
    g.append(l, control);
    return g;
  }

  private makeUndoRedo(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'group';
    const undoBtn = document.createElement('button');
    undoBtn.className = 'btn ghost';
    undoBtn.textContent = '↶';
    undoBtn.title = '실행 취소 (⌘Z)';
    undoBtn.addEventListener('click', () => undo());
    const redoBtn = document.createElement('button');
    redoBtn.className = 'btn ghost';
    redoBtn.textContent = '↷';
    redoBtn.title = '다시 실행 (⌘⇧Z)';
    redoBtn.addEventListener('click', () => redo());
    const sync = (): void => {
      undoBtn.disabled = !canUndo();
      redoBtn.disabled = !canRedo();
    };
    sync();
    subscribe(sync);
    wrap.append(undoBtn, redoBtn);
    return wrap;
  }

  private makeZoomControls(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'group';
    const minus = document.createElement('button');
    minus.className = 'btn ghost';
    minus.textContent = '−';
    minus.title = '캔버스 축소';
    minus.addEventListener('click', () => setZoom(getState().zoom / 1.2));
    const label = document.createElement('span');
    label.className = 'zoom-label';
    label.title = '클릭해서 100%';
    label.addEventListener('click', () => setZoom(1));
    const plus = document.createElement('button');
    plus.className = 'btn ghost';
    plus.textContent = '+';
    plus.title = '캔버스 확대';
    plus.addEventListener('click', () => setZoom(getState().zoom * 1.2));
    wrap.append(minus, label, plus);
    this.zoomLabel = label;
    return wrap;
  }

  private makeCustomSize(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'group';
    const w = document.createElement('input');
    w.type = 'number';
    w.placeholder = 'W';
    w.title = '캔버스 width (px)';
    w.style.width = '64px';
    const h = document.createElement('input');
    h.type = 'number';
    h.placeholder = 'H';
    h.title = '캔버스 height (px)';
    h.style.width = '64px';
    this.wInput = w;
    this.hInput = h;

    const apply = (from: 'w' | 'h'): void => {
      const ratio = getState().project.canvas.ratio;
      const ar = aspectFor(ratio);
      const wn = parseInt(w.value, 10);
      const hn = parseInt(h.value, 10);
      if (!Number.isFinite(wn) || wn < 50) return;
      let nextW = wn;
      let nextH: number;
      if (ar) {
        if (from === 'w') {
          nextH = Math.max(50, Math.round(wn / ar));
        } else {
          if (!Number.isFinite(hn) || hn < 50) return;
          nextH = hn;
          nextW = Math.max(50, Math.round(hn * ar));
        }
      } else {
        if (!Number.isFinite(hn) || hn < 50) return;
        nextH = hn;
      }
      updateProject((p) => ({
        ...p,
        canvas: { ...p.canvas, customSize: { w: nextW, h: nextH } },
      }));
    };
    w.addEventListener('input', () => apply('w'));
    h.addEventListener('input', () => apply('h'));

    wrap.append(w, document.createTextNode('×'), h);
    return wrap;
  }

  private makeRatioSelect(): HTMLSelectElement {
    const s = document.createElement('select');
    for (const [val, label] of [
      ['9:16', '9:16'],
      ['1:1', '1:1'],
      ['4:5', '4:5'],
      ['16:9', '16:9'],
      ['free', '자유'],
    ] as Array<[Ratio, string]>) {
      const o = document.createElement('option');
      o.value = val;
      o.textContent = label;
      s.appendChild(o);
    }
    s.addEventListener('change', () => {
      const ratio = s.value as Ratio;
      updateProject((p) => {
        const next = { ...p, canvas: { ...p.canvas, ratio } };
        if (ratio !== 'free') {
          const ar = aspectFor(ratio);
          const cur = p.canvas.customSize;
          if (cur && ar) {
            next.canvas.customSize = { w: cur.w, h: Math.round(cur.w / ar) };
          } else {
            next.canvas.customSize = RATIO_DIMS[ratio];
          }
        }
        return next;
      });
    });
    this.ratioSelect = s;
    return s;
  }

  private makeSafeSelect(): HTMLSelectElement {
    const s = document.createElement('select');
    const opts: [string, string][] = [
      ['none', 'none'],
      ['1:1', '1:1'],
      ['4:5', '4:5'],
      ['16:9', '16:9'],
      ['9:16', '9:16'],
    ];
    for (const [val, label] of opts) {
      const o = document.createElement('option');
      o.value = val;
      o.textContent = label;
      s.appendChild(o);
    }
    s.addEventListener('change', () => {
      const v = s.value === 'none' ? null : (s.value as Ratio);
      updateProject((p) => ({ ...p, canvas: { ...p.canvas, safeRatio: v } }));
    });
    this.safeSelect = s;
    return s;
  }

  private makeDurationInput(): HTMLInputElement {
    const i = document.createElement('input');
    i.type = 'number';
    i.min = '500';
    i.step = '100';
    i.style.width = '76px';
    i.addEventListener('input', () => {
      const v = parseInt(i.value, 10);
      if (Number.isNaN(v) || v < 500) return;
      updateProject((p) => ({ ...p, duration: v }));
      const ph = getState().playhead;
      if (ph > v) setPlayhead(v);
    });
    this.durationInput = i;
    return i;
  }
}
